import { Button, Grow, Stack, TextField, Typography } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useTokenMaker } from "../../hooks";
const logo = "/airplx-logo-transparent-square-no-text.png";

export const ForgotPassword: React.FC<{}> = () => {
  const { t } = useTranslation();
  const { url: tokenMakerURL } = useTokenMaker();
  const location = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = React.useState<string>(location.state?.email || "");
  const [sent, setSent] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>("");

  const handleSendReset = async () => {
    if (!email) {
      setError(t('screens.forgotPassword.errors.emailRequired'));
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t('screens.forgotPassword.errors.invalidEmail'));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${tokenMakerURL}/send-password-reset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSent(true);
    } catch (err) {
      setError(t('screens.forgotPassword.errors.sendFailed'));
      console.error("Error sending password reset:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        height="60vh"
        width="100vw"
      >
        <Stack direction="column" width={450} spacing={3}>
          <Grow in={!sent}>
            <Stack direction="column" spacing={3} alignItems="center">
              <Stack spacing={1} alignItems="center">
                <Typography variant="h5">{t('screens.forgotPassword.title')}</Typography>
                <Typography variant="body1" color="text.secondary" align="center">
                  {t('screens.forgotPassword.subtitle')}
                </Typography>
              </Stack>

              <TextField
                fullWidth
                label={t('screens.forgotPassword.emailLabel')}
                variant="outlined"
                type="email"
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSendReset()}
                value={email}
                error={Boolean(error)}
                helperText={error}
                disabled={loading}
                autoFocus
              />

              <Button
                disabled={!email || loading}
                variant="contained"
                color="primary"
                onClick={handleSendReset}
                fullWidth
                sx={{ borderRadius: 1 }}
              >
                {loading ? t('screens.forgotPassword.sendingButton') : t('screens.forgotPassword.sendButton')}
              </Button>

              <Button
                size="small"
                onClick={() => navigate("/login")}
              >
                {t('screens.forgotPassword.backToLogin')}
              </Button>
            </Stack>
          </Grow>
          <Grow in={sent}>
            <Stack direction="column" spacing={2} alignItems="center">
              <Typography variant="h5">{t('screens.forgotPassword.successTitle')}</Typography>
              <Typography variant="body1" color="text.secondary" align="center">
                {t('screens.forgotPassword.successMessage')}<br />
                <strong>{email}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                {t('screens.forgotPassword.expiryNote')}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate("/login")}
                sx={{ mt: 2 }}
              >
                {t('screens.forgotPassword.backToLogin')}
              </Button>
            </Stack>
          </Grow>
        </Stack>
      </Stack>
      <img
        src={logo}
        style={{ position: "fixed", bottom: 15, right: 15, width: 50 }}
      />
    </>
  );
};
